import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaChartLine, FaUsers, FaExchangeAlt, FaPercentage, FaYoutube, FaInstagram, FaEye, FaThumbsUp, FaComment, FaShare, FaBookmark, FaDownload, FaFileCsv, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, prepareExportData, exportSummaryStats } from '../../../utils/exportUtils';
// import { truncate, formatNumberWithCommas, formatPercentage } from '../../utils/textHelpers';

const CampaignAffiliateStats = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d, all
  const [activeTab, setActiveTab] = useState('affiliate-links'); // affiliate-links, youtube, instagram

  const fetchCampaignAndAffiliates = useCallback( async () => {
    try {
      setLoading(true);
      
      // Fetch campaign details
      const campaignResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const campaignData = await campaignResponse.json();

      if (!campaignData.success) {
        throw new Error(campaignData.message || 'Failed to fetch campaign details');
      }

      setCampaign(campaignData.campaign);

      // Initialize variables
      let influencerStats = {};

      // Fetch affiliate links with influencer data
      try {
        const linksResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/affiliate-links`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const linksData = await linksResponse.json();

        if (linksData.success && linksData.data) {
          const affiliateLinks = linksData.data || [];
          
          // Process affiliate link data
          affiliateLinks.forEach(link => {
            if (link.userId) {
              influencerStats[link.userId] = {
                _id: link._id,
                userId: link.userId,
                firstName: link.firstName || 'Unknown',
                lastName: link.lastName || 'User',
                username: link.username || 'unknown_user',
                email: link.email || 'unknown@example.com',
                totalVisits: link.visits || 0,
                uniqueVisits: link.uniqueVisits || 0,
                conversions: link.conversions || 0,
                youtubeStats: {
                  totalVideos: 0,
                  approvedVideos: 0,
                  totalViews: 0,
                  totalLikes: 0,
                  totalComments: 0,
                  totalEngagements: 0,
                  videos: []
                },
                instagramStats: {
                  totalPosts: 0,
                  approvedPosts: 0,
                  totalLikes: 0,
                  totalComments: 0,
                  totalShares: 0,
                  totalSaves: 0,
                  totalReachs: 0,
                  totalEngagements: 0,
                  posts: []
                }
              };
            }
          });
        }
      } catch (linksError) {
        console.error('Error fetching affiliate links:', linksError);
      }

      // Fetch YouTube media with individual influencer data
      try {
        const youtubeResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/youtube-media`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const youtubeData = await youtubeResponse.json();

        if (youtubeData.success && youtubeData.data) {
          youtubeData.data.forEach(influencer => {
            if (influencer.userId) {
              if (!influencerStats[influencer.userId]) {
                // Create new influencer entry if it doesn't exist
                influencerStats[influencer.userId] = {
                  _id: influencer._id,
                  userId: influencer.userId,
                  firstName: influencer.firstName || 'Unknown',
                  lastName: influencer.lastName || 'User',
                  username: influencer.username || 'unknown_user',
                  email: influencer.email || 'unknown@example.com',
                  totalVisits: 0,
                  uniqueVisits: 0,
                  conversions: 0,
                  youtubeStats: {
                    totalVideos: 0,
                    approvedVideos: 0,
                    totalViews: 0,
                    totalLikes: 0,
                    totalComments: 0,
                    totalEngagements: 0,
                    videos: []
                  },
                  instagramStats: {
                    totalPosts: 0,
                    approvedPosts: 0,
                    totalLikes: 0,
                    totalComments: 0,
                    totalShares: 0,
                    totalSaves: 0,
                    totalReachs: 0,
                    totalEngagements: 0,
                    posts: []
                  }
                };
              }
              
              // Update YouTube stats for this influencer
              influencerStats[influencer.userId].youtubeStats = influencer.youtubeStats;
            }
          });
        }
      } catch (youtubeError) {
        console.error('Error fetching YouTube data:', youtubeError);
      }

      // Fetch Instagram media with individual influencer data
      try {
        const instagramResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/instagram-media`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const instagramData = await instagramResponse.json();

        if (instagramData.success && instagramData.data) {
          instagramData.data.forEach(influencer => {
            if (influencer.userId) {
              if (!influencerStats[influencer.userId]) {
                // Create new influencer entry if it doesn't exist
                influencerStats[influencer.userId] = {
                  _id: influencer._id,
                  userId: influencer.userId,
                  firstName: influencer.firstName || 'Unknown',
                  lastName: influencer.lastName || 'User',
                  username: influencer.username || 'unknown_user',
                  email: influencer.email || 'unknown@example.com',
                  totalVisits: 0,
                  uniqueVisits: 0,
                  conversions: 0,
                  youtubeStats: {
                    totalVideos: 0,
                    approvedVideos: 0,
                    totalViews: 0,
                    totalLikes: 0,
                    totalComments: 0,
                    totalEngagements: 0,
                    videos: []
                  },
                  instagramStats: {
                    totalPosts: 0,
                    approvedPosts: 0,
                    totalLikes: 0,
                    totalComments: 0,
                    totalShares: 0,
                    totalSaves: 0,
                    totalReachs: 0,
                    totalEngagements: 0,
                    posts: []
                  }
                };
              }
              
              // Update Instagram stats for this influencer
              influencerStats[influencer.userId].instagramStats = influencer.instagramStats;
            }
          });
        }
      } catch (instagramError) {
        console.error('Error fetching Instagram data:', instagramError);
      }

      // Convert to array and sort by conversions
      const sortedInfluencers = Object.values(influencerStats).sort((a, b) => b.conversions - a.conversions);
      setAffiliates(sortedInfluencers);

    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      toast.error(error.message || 'Failed to fetch campaign statistics');
    } finally {
      setLoading(false);
    }
  },[campaignId]);

  useEffect(() => {
    fetchCampaignAndAffiliates();
  }, [fetchCampaignAndAffiliates, timeRange]);
  

  // Calculate summary statistics
  const summaryStats = {
    totalAffiliates: affiliates.length,
    totalVisits: affiliates.reduce((sum, aff) => sum + (aff.totalVisits || 0), 0),
    totalConversions: affiliates.reduce((sum, aff) => sum + (aff.conversions || 0), 0),
    avgConversionRate: (() => {
      const totalVisits = affiliates.reduce((sum, aff) => sum + (aff.totalVisits || 0), 0);
      const totalConversions = affiliates.reduce((sum, aff) => sum + (aff.conversions || 0), 0);
      return totalVisits > 0 ? ((totalConversions / totalVisits) * 100).toFixed(2) : '0.00';
    })(),
    totalYouTubeViews: affiliates.reduce((sum, aff) => sum + (aff.youtubeStats?.totalViews || 0), 0),
    totalYouTubeEngagements: affiliates.reduce((sum, aff) => sum + (aff.youtubeStats?.totalEngagements || 0), 0),
    totalInstagramReachs: affiliates.reduce((sum, aff) => sum + (aff.instagramStats?.totalReachs || 0), 0),
    totalInstagramEngagements: affiliates.reduce((sum, aff) => sum + (aff.instagramStats?.totalEngagements || 0), 0)
  };

  // Export functions
  const handleExportCSV = () => {
    try {
      const exportData = prepareExportData(
        campaign, 
        affiliates, 
        'Infuber Company', // Company name - you can make this dynamic if available
        campaign?.brandName || 'Unknown Brand' // Brand name from campaign
      );
      
      if (exportData.length === 0) {
        toast.error('No data available for export');
        return;
      }

      const filename = `campaign-affiliate-stats-${campaign?.name?.replace(/\s+/g, '-') || 'unknown'}-${new Date().toISOString().split('T')[0]}`;
      const result = exportToCSV(exportData, filename);
      
      if (result.success) {
        toast.success('CSV exported successfully!');
      } else {
        toast.error(result.message || 'Failed to export CSV');
      }
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const exportData = prepareExportData(
        campaign, 
        affiliates, 
        'Infuber Company', // Company name - you can make this dynamic if available
        campaign?.brandName || 'Unknown Brand' // Brand name from campaign
      );
      
      if (exportData.length === 0) {
        toast.error('No data available for export');
        return;
      }

      const filename = `campaign-affiliate-stats-${campaign?.name?.replace(/\s+/g, '-') || 'unknown'}-${new Date().toISOString().split('T')[0]}`;
      const result = exportToExcel(exportData, filename);
      
      if (result.success) {
        toast.success('Excel file exported successfully!');
      } else {
        toast.error(result.message || 'Failed to export Excel file');
      }
    } catch (error) {
      console.error('Export Excel error:', error);
      toast.error('Failed to export Excel file. Please try again.');
    }
  };

  const handleExportSummary = () => {
    try {
      if (!campaign || !summaryStats) {
        toast.error('No summary data available for export');
        return;
      }

      const filename = `campaign-summary-${campaign?.name?.replace(/\s+/g, '-') || 'unknown'}-${new Date().toISOString().split('T')[0]}`;
      const result = exportSummaryStats(summaryStats, campaign, filename);
      
      if (result.success) {
        toast.success('Summary statistics exported successfully!');
      } else {
        toast.error(result.message || 'Failed to export summary statistics');
      }
    } catch (error) {
      console.error('Export summary error:', error);
      toast.error('Failed to export summary statistics. Please try again.');
    }
  };

  // Test export function for development
  // const handleTestExport = () => {
  //   try {
  //     const result = testExport();
  //     toast.success('Test export completed! Check console for details.');
  //     console.log('Test export completed:', result);
  //   } catch (error) {
  //     console.error('Test export error:', error);
  //     toast.error('Test export failed. Check console for details.');
  //   }
  // };

  // Preview export format
  // const handlePreviewExport = () => {
  //   try {
  //     const exportData = prepareExportData(
  //       campaign, 
  //       affiliates, 
  //       'Infuber Company',
  //       campaign?.brandName || 'Unknown Brand'
  //     );
      
  //     if (exportData.length === 0) {
  //       toast.error('No data available for preview');
  //       return;
  //     }

  //     const preview = previewExportFormat(exportData);
  //     console.log('Export Preview:', preview);
      
  //     // Show preview in a toast or alert
  //     toast.info('Export preview generated! Check console for details.', {
  //       autoClose: 5000,
  //       position: "top-right"
  //     });
      
  //     // You can also show this in a modal or alert if preferred
  //     alert('Export Preview:\n\n' + preview);
      
  //   } catch (error) {
  //     console.error('Preview export error:', error);
  //     toast.error('Failed to generate preview. Please try again.');
  //   }
  // };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full mt-6">
      <div className="container mx-auto p-4 md:p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 ">
          <div className="flex items-center mb-4 md:mb-0">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 p-2 rounded-full hover:bg-gray-200 lg:mr-4"
            >
              <FaArrowLeft size={22} />
            </button>
            <div>
              <h2 className="lg:text-2xl text-xl font-bold text-gray-800">
                {campaign?.name} - Affiliate Statistics
              </h2>
              {/* <p className="text-sm text-gray-500 mt-1">{truncate(campaign?.description, 20)}</p> */}
            </div>
          </div>

          {/* Time Range Selector */}
          <div className="flex space-x-2">
            {['7d', '30d', '90d', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 rounded-full text-sm ${timeRange === range
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {range === 'all' ? 'All Time' : range}
              </button>
            ))}
          </div>
        </div>

        {/* Export Buttons Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Export Campaign Data</h3>
              <p className="text-sm text-gray-600">Download campaign affiliate statistics in various formats</p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportCSV}
                disabled={!affiliates || affiliates.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Export detailed data to CSV format"
              >
                <FaFileCsv />
                Export CSV
              </button>
              
              <button
                onClick={handleExportExcel}
                disabled={!affiliates || affiliates.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Export detailed data to Excel format"
              >
                <FaFileExcel />
                Export Excel
              </button>
              
              <button
                onClick={handleExportSummary}
                disabled={!campaign || !summaryStats}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Export summary statistics to Excel"
              >
                <FaDownload />
                Export Summary
              </button>
              
              {/* Test Export Button - Remove in production */}
              {/* <button
                onClick={handleTestExport}
                className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                title="Test export functionality (development only)"
              >
                Test Export
              </button> */}

              {/* Preview Export Button - Remove in production */}
              {/* <button
                onClick={handlePreviewExport}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                title="Preview export data (development only)"
              >
                Preview Export
              </button> */}
            </div>
          </div>
          
          {(!affiliates || affiliates.length === 0) && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Export buttons are disabled because there's no affiliate data available for this campaign.
              </p>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-500">
                <FaUsers size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Influencers</p>
                <p className="text-2xl font-semibold">{summaryStats.totalAffiliates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                <FaChartLine size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Visits</p>
                <p className="text-2xl font-semibold">{summaryStats.totalVisits.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-500">
                <FaExchangeAlt size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Conversions</p>
                <p className="text-2xl font-semibold">{summaryStats.totalConversions.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-500">
                <FaPercentage size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Avg. Conversion Rate</p>
                <p className="text-2xl font-semibold">{summaryStats.avgConversionRate}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-500">
                <FaYoutube size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">YouTube Views</p>
                <p className="text-2xl font-semibold">{summaryStats.totalYouTubeViews.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-500">
                <FaThumbsUp size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">YouTube Engagements</p>
                <p className="text-2xl font-semibold">{summaryStats.totalYouTubeEngagements.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                <FaInstagram size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Instagram Reachs</p>
                <p className="text-2xl font-semibold">{summaryStats.totalInstagramReachs.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                <FaThumbsUp size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Instagram Engagements</p>
                <p className="text-2xl font-semibold">{summaryStats.totalInstagramEngagements.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('affiliate-links')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'affiliate-links'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <FaChartLine className="inline mr-2" />
                Affiliate Links
              </button>
              <button
                onClick={() => setActiveTab('youtube')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'youtube'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <FaYoutube className="inline mr-2" />
                YouTube Media
              </button>
              <button
                onClick={() => setActiveTab('instagram')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'instagram'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <FaInstagram className="inline mr-2" />
                Instagram Media
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {activeTab === 'affiliate-links' && (
              <>
                <h4 className="text-lg font-semibold mb-4">Affiliate Link Performance</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Affiliate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Visits
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unique Visits
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversion Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                  <FaUser className="text-orange-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.totalVisits || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.uniqueVisits || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.conversions || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.totalVisits > 0
                                ? ((affiliate.conversions / affiliate.totalVisits) * 100).toFixed(2)
                                : '0.00'}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'youtube' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaYoutube className="text-red-500 mr-2" />
                  YouTube Media Performance
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Influencer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Videos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaEye className="inline mr-1" />
                          Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaThumbsUp className="inline mr-1" />
                          Likes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaComment className="inline mr-1" />
                          Comments
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Engagements
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                  <FaUser className="text-red-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.youtubeStats?.totalVideos || 0}
                              <span className="text-xs text-gray-500 ml-1">
                                ({affiliate.youtubeStats?.approvedVideos || 0} approved)
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalViews || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalLikes || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalComments || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalEngagements || 0).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'instagram' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaInstagram className="text-pink-500 mr-2" />
                  Instagram Media Performance
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Influencer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Posts
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaEye className="inline mr-1" />
                          Reachs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaThumbsUp className="inline mr-1" />
                          Likes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaComment className="inline mr-1" />
                          Comments
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaShare className="inline mr-1" />
                          Shares
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaBookmark className="inline mr-1" />
                          Saves
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Engagements
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                                  <FaUser className="text-pink-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.instagramStats?.totalPosts || 0}
                              <span className="text-xs text-gray-500 ml-1">
                                ({affiliate.instagramStats?.approvedPosts || 0} approved)
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalReachs || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalLikes || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalComments || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalShares || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalSaves || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalEngagements || 0).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default CampaignAffiliateStats; 